import { SystemStyleObject } from "@chakra-ui/react";
import {
  CreatableSelect,
  FormatOptionLabelMeta,
  MenuPlacement,
  MultiValue,
  Select,
} from "chakra-react-select";
import {
  CreateUserListMutation,
  CreateUserListMutationVariables,
  UserFragment,
  UserListQuery,
} from "gql/graphql";
import { useUsers } from "modules/providers/users-provider";
import React, { memo, useCallback, useEffect, useState } from "react";
import colors from "app/colors";
import { useApolloClient, useMutation } from "@apollo/client";
import CREATE_USER_LIST_MUTATION from "modules/messaging/_networking/create-user-list";
import * as EmailValidator from "email-validator";
import USER_LIST_QUERY from "modules/providers/users-provider/networking/user-list";
import EmailUserOptionLabel from "./email-user-option-label";
import { getUserFullName } from "modules/utils/Functions";

interface IProps {
  defaultValue?: readonly UserFragment[];
  onChange: (value: UserFragment[]) => void;
  menuPlacement?: MenuPlacement;
  allowCreation: boolean;
  autoFocus?: boolean;
}

const EmailUserSelector = ({
  defaultValue = [],
  onChange,
  menuPlacement,
  allowCreation,
  autoFocus = false,
}: IProps) => {
  const { cache } = useApolloClient();
  const { emailUsers } = useUsers();
  const [value, setValue] = useState<readonly UserFragment[]>(defaultValue);

  const [createUserListAPI] = useMutation<
    CreateUserListMutation,
    CreateUserListMutationVariables
  >(CREATE_USER_LIST_MUTATION);

  const _onChange = useCallback(
    (value: MultiValue<UserFragment>) => {
      setValue(value);
      onChange([...value]);
    },
    [onChange],
  );

  useEffect(() => {
    if (defaultValue) {
      _onChange(defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onItemCreated = useCallback(
    (newValue: string) => {
      if (EmailValidator.validate(newValue) === true) {
        createUserListAPI({
          variables: {
            input: {
              userInputList: [
                {
                  email: newValue.trim(),
                },
              ],
            },
          },
          onCompleted: (data) => {
            const user =
              data.createUserList.length === 1
                ? data.createUserList[0]
                : undefined;
            if (user) {
              cache.updateQuery<UserListQuery>(
                {
                  query: USER_LIST_QUERY,
                },
                (data) => {
                  if (!data) {
                    return data;
                  }
                  return {
                    ...data,
                    userList: [...data.userList, user],
                  };
                },
              );
              _onChange([...value, user]);
            }
          },
        });
      }
    },
    [_onChange, cache, createUserListAPI, value],
  );

  const isValidNewOption = useCallback(
    (inputValue: string) => EmailValidator.validate(inputValue),
    [],
  );

  const renderEmailUser = useCallback(
    (emailUser: UserFragment, meta: FormatOptionLabelMeta<UserFragment>) => (
      <EmailUserOptionLabel user={emailUser} search={meta.inputValue} />
    ),
    [],
  );

  const getOptionLabel = useCallback(
    (user: UserFragment) => `${user.email} ${getUserFullName(user)}`.trim(),
    [],
  );

  const getOptionValue = useCallback((user: UserFragment) => user.id, []);

  if (allowCreation) {
    return (
      <CreatableSelect
        options={emailUsers}
        autoFocus={autoFocus}
        onChange={_onChange}
        onCreateOption={onItemCreated}
        components={{
          DropdownIndicator: () => null,
          IndicatorSeparator: () => null,
        }}
        placeholder="Click to add email addresses"
        menuPlacement={menuPlacement || "top"}
        chakraStyles={{
          placeholder: (provided: SystemStyleObject): SystemStyleObject => ({
            ...provided,
            fontStyle: "italic",
            color: colors.placeholderText,
          }),
          container: (provided: SystemStyleObject): SystemStyleObject => ({
            ...provided,
            width: "full",
          }),
          multiValue: (provided: SystemStyleObject): SystemStyleObject => ({
            ...provided,
            borderRadius: "60px",
            borderWidth: "1px",
            borderColor: colors.blue,
            backgroundColor: colors.white,
          }),
          singleValue: (provided: SystemStyleObject): SystemStyleObject => ({
            ...provided,
            borderRadius: "60px",
            height: "24px",
            backgroundColor: colors.blueLight,
          }),
        }}
        isMulti
        value={value}
        formatOptionLabel={renderEmailUser}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        isValidNewOption={isValidNewOption}
      />
    );
  }

  return (
    <Select
      options={emailUsers}
      autoFocus={autoFocus}
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
      }}
      placeholder="Click to add email addresses"
      chakraStyles={{
        placeholder: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          fontStyle: "italic",
          color: colors.placeholderText,
        }),
      }}
      isMulti
      formatOptionLabel={renderEmailUser}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      onChange={_onChange}
      menuPlacement={menuPlacement || "top"}
      value={value}
    />
  );
};

export default memo(EmailUserSelector);
